import React from "react";
import {
  ReceivingInvoiceLine,
  SourceWipsCardProps,
  WipSource,
} from "../../types/traceability";
import { kgFmt, lbsFmt, pcsFmt } from "../../shared/formatting";
import { formatDate } from "../../shared/utilityTsx";
import CopyableIdentifier from "../controls/CopyableIdentifier";

function Metric({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="text-sm font-medium text-gray-600 mb-1">{label}</div>
      <div className="text-xl font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function ReceivingInvoiceBlock({ line }: { line: ReceivingInvoiceLine }) {
  const location = [line.warehouse_location_name, line.warehouse_name]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Receiving Source
            </h3>
            <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
              Supplier
            </span>
          </div>
          {line.receiving_invoice_number && (
            <p className="text-sm text-gray-600 mt-1">
              Invoice: {line.receiving_invoice_number}
            </p>
          )}
          {line.supplier_name && (
            <p className="text-sm text-gray-600">
              Supplier: {line.supplier_name}
            </p>
          )}
          {location && (
            <p className="text-sm text-gray-600">Location: {location}</p>
          )}
        </div>
        {line.receiving_invoice_id ? (
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            onClick={() =>
              window.open(`/receiving/${line.receiving_invoice_id}`, "_blank")
            }
          >
            View Receiving
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {line.receiving_date && (
          <Metric label="Receiving Date" value={formatDate(line.receiving_date)} />
        )}
        {line.total_received_kg !== undefined && (
          <Metric label="Total Received (kg)" value={kgFmt(line.total_received_kg)} />
        )}
        {line.total_received_lbs !== undefined && (
          <Metric label="Total Received (lbs)" value={lbsFmt(line.total_received_lbs)} />
        )}
        {line.total_received_pcs !== undefined && (
          <Metric label="Total Received (pcs)" value={pcsFmt(line.total_received_pcs)} />
        )}
        {line.receiving_carton_count !== undefined && (
          <Metric label="Carton Count" value={line.receiving_carton_count || 0} />
        )}
      </div>
    </div>
  );
}

function WipSourceBlock({ source }: { source: WipSource }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            WIP {source.wip_lot_number || `#${source.wip_id}`}
          </h3>
          <p className="text-sm text-gray-600">WIP ID: {source.wip_id}</p>
        </div>
        {source.wip_id ? (
          <button
            type="button"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            onClick={() => window.open(`/wips/${source.wip_id}`, "_blank")}
          >
            View WIP
          </button>
        ) : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {source.total_produced_kg !== undefined && (
          <Metric label="Total Produced (kg)" value={kgFmt(source.total_produced_kg)} />
        )}
        {source.total_produced_pcs !== undefined && (
          <Metric label="Total Produced (pcs)" value={pcsFmt(source.total_produced_pcs)} />
        )}
        {source.carton_count !== undefined && (
          <Metric label="Carton Count" value={source.carton_count || 0} />
        )}
      </div>
    </div>
  );
}

export default function SourceWipsCard({
  lotNumber,
  source,
}: SourceWipsCardProps) {
  if (!source) return null;
  const receivingInvoices = source.receiving_invoices ?? [];

  return (
    <div className="card-custom bg-white p-6">
      <div className="mb-4">
        <h2 className="flex items-center gap-1 text-2xl font-semibold text-gray-900">
          <span>Lot Number:</span>
          <CopyableIdentifier value={lotNumber} description="lot number" />
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Source WIP or Receiving source for this lot number.
        </p>
      </div>

      <div className="space-y-4">
        {source.is_wip && <WipSourceBlock source={source} />}
        {!source.is_wip && receivingInvoices.length === 0 ? (
          <p className="text-sm text-gray-600">
            No receiving invoice rows were returned for this lot.
          </p>
        ) : null}
        {receivingInvoices.map((line, index) => (
            <ReceivingInvoiceBlock
              key={`${line.receiving_invoice_id}-${line.warehouse_id ?? "warehouse"}-${line.warehouse_location_id ?? index}`}
              line={line}
            />
          ))}
      </div>
    </div>
  );
}
